import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../../settings/local_settings';
import { APIClient } from '../../../utils/APIClient';
import { Spinner } from '../../Spinner';

function EditableEmail({ label, value, fieldName, onSaved }) {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');
    const [saving, setSaving] = useState(false);

    const handleSave = () => {
        setSaving(true);
        new APIClient(API_URL + 'agency/subscription/billing-emails/')
            .call('PATCH', { [fieldName]: editValue })
            .then((data) => {
                setSaving(false);
                if (data.status === 'OK') {
                    setEditing(false);
                    if (onSaved) onSaved(data.data);
                }
            })
            .catch(() => setSaving(false));
    };

    if (!editing) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                <span style={{ color: '#B6B6B7' }}>{label}:</span>
                <strong style={{ color: '#4A4A4A' }}>{value || '-'}</strong>
                <button
                    onClick={() => { setEditValue(value || ''); setEditing(true); }}
                    style={{
                        border: 'none', background: 'none', cursor: 'pointer',
                        color: '#FF470F', fontSize: '0.8em', padding: '0.2em 0.4em'
                    }}
                >
                    {t('agency_subscription.edit')}
                </button>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
            <span style={{ color: '#B6B6B7' }}>{label}:</span>
            <input
                type="email"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                style={{
                    border: '1px solid #E2E2E2', borderRadius: '0.3em',
                    padding: '0.2em 0.5em', fontSize: '0.87em', width: '220px'
                }}
            />
            {saving ? (
                <Spinner color="positive small" />
            ) : (
                <>
                    <button className="btn -alt" onClick={handleSave}
                        style={{ fontSize: '0.75em', padding: '0.2em 0.6em' }}>
                        {t('agency_subscription.save')}
                    </button>
                    <button onClick={() => setEditing(false)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#B6B6B7', fontSize: '0.8em' }}>
                        {t('agency_subscription.cancel_action')}
                    </button>
                </>
            )}
        </div>
    );
}

const PAYMENT_METHOD_DISPLAY = {
    'MercadoPago AR - Pago automático': 'MercadoPago - Plan de suscripción activo',
};

function displayPaymentMethod(pm) {
    return PAYMENT_METHOD_DISPLAY[pm] || pm;
}

function PaymentMethodBadge({ paymentMethod }) {
    return (
        <span style={{
            backgroundColor: '#440631',
            color: '#fff',
            padding: '0.2em 0.7em',
            borderRadius: '0.3em',
            fontSize: '0.8em',
            fontWeight: 600,
            fontFamily: 'Source Sans Pro, sans-serif'
        }}>
            {displayPaymentMethod(paymentMethod)}
        </span>
    );
}

function SubscriptionStatusBadge({ isActive, label }) {
    const { t } = useTranslation();
    const color = isActive ? '#7CB502' : '#DF0D00';
    const text = isActive
        ? t('agency_subscription.subscription_active')
        : t('agency_subscription.subscription_inactive');

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
            <span style={{ color: '#B6B6B7' }}>{label}:</span>
            <span style={{
                backgroundColor: color,
                color: '#fff',
                padding: '0.15em 0.5em',
                borderRadius: '0.3em',
                fontSize: '0.75em',
                fontWeight: 600
            }}>
                {text}
            </span>
        </div>
    );
}

const COUPON_METHODS = [
    'Paypal - Envío manual',
    'MercadoPago - Cupón'
];

function isCouponMethod(pm) {
    return COUPON_METHODS.includes(pm);
}

function formatFeeWithIva(feeText, invoiceType) {
    if (!feeText) return feeText;
    // Remove "Dashbo " or similar prefix
    let text = feeText.replace(/^\w+\s+/, '');
    // Format money: $109301.00 → $ 109.301
    text = text.replace(/\$[\d.]+(?:\.\d+)?/g, (match) => {
        const num = parseFloat(match.replace('$', ''));
        if (isNaN(num)) return match;
        return '$ ' + formatArgNumber(num);
    });
    if (invoiceType === 'A') {
        text += ' + IVA';
    }
    return text;
}

function formatArgNumber(num) {
    const hasDecimals = num % 1 !== 0;
    if (hasDecimals) {
        return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return num.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function BillingInfoHeader({ billingData, onEmailsUpdated }) {
    const { t } = useTranslation();

    if (!billingData || !billingData.available) return null;

    const { client, active_comm_agree: ca } = billingData;
    if (!ca || Object.keys(ca).length === 0) return null;

    const pm = ca.payment_method;
    const showCouponEmail = isCouponMethod(pm);

    return (
        <div style={{
            backgroundColor: '#F9F9F9',
            borderRadius: '0.3em',
            padding: '0.85em 1em',
            marginBottom: '1em'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75em',
                marginBottom: '0.6em'
            }}>
                <h6 style={{
                    fontFamily: 'Source Sans Pro, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.87em',
                    color: '#4A4A4A',
                    margin: 0
                }}>
                    {t('agency_subscription.active_agreement')}
                </h6>
                {pm && <PaymentMethodBadge paymentMethod={pm} />}
            </div>

            <div style={{
                display: 'flex',
                gap: '2em',
                flexWrap: 'wrap',
                fontSize: '0.87em',
                fontFamily: 'Source Sans Pro, sans-serif',
                marginBottom: '0.6em'
            }}>
                {ca.currency && (
                    <div><span style={{ color: '#B6B6B7' }}>{t('agency_subscription.currency')}:</span> <strong style={{ color: '#4A4A4A' }}>{ca.currency}</strong></div>
                )}
                {ca.fee_text && (
                    <div><span style={{ color: '#B6B6B7' }}>{t('agency_subscription.fees')}:</span> <strong style={{ color: '#4A4A4A' }}>{formatFeeWithIva(ca.fee_text, ca.invoice_type)}</strong></div>
                )}
            </div>

            {/* Emails editables */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.4em',
                fontSize: '0.87em',
                fontFamily: 'Source Sans Pro, sans-serif'
            }}>
                {client && (
                    <EditableEmail
                        label={t('agency_subscription.invoice_email')}
                        value={client.emails_for_invoices}
                        fieldName="emails_for_invoices"
                        onSaved={onEmailsUpdated}
                    />
                )}
                {showCouponEmail && client && (
                    <EditableEmail
                        label={t('agency_subscription.payment_link_email')}
                        value={client.email_for_payment_link}
                        fieldName="email_for_payment_link"
                        onSaved={onEmailsUpdated}
                    />
                )}
            </div>

            {/* Info condicional por medio de pago */}
            {pm === 'Paypal - Suscripción' && ca.paypal_subscription_id && (
                <div style={{ marginTop: '0.6em', fontSize: '0.87em', fontFamily: 'Source Sans Pro, sans-serif' }}>
                    <SubscriptionStatusBadge
                        isActive={ca.paypal_subscription_active === true}
                        label={t('agency_subscription.paypal_subscription_status')}
                    />
                </div>
            )}

            {pm === 'MercadoPago AR - Pago automático' && ca.mercado_pago_suscriptor_data && (
                <div style={{ marginTop: '0.6em', fontSize: '0.87em', fontFamily: 'Source Sans Pro, sans-serif' }}>
                    <span style={{ color: '#B6B6B7' }}>{t('agency_subscription.mp_subscription_info')}:</span>{' '}
                    <strong style={{ color: '#4A4A4A' }}>{ca.mercado_pago_suscriptor_data}</strong>
                </div>
            )}

            {pm === 'Transferencia Bancaria' && ca.bank_transfer_data && (
                <div style={{
                    marginTop: '0.6em', fontSize: '0.87em',
                    fontFamily: 'Source Sans Pro, sans-serif',
                    backgroundColor: '#fff',
                    border: '1px solid #E2E2E2',
                    borderRadius: '0.3em',
                    padding: '0.6em 0.8em'
                }}>
                    <div style={{ color: '#B6B6B7', marginBottom: '0.3em', fontWeight: 600 }}>
                        {t('agency_subscription.bank_transfer_details')}
                    </div>
                    <div style={{ color: '#4A4A4A', whiteSpace: 'pre-line' }}>
                        {ca.bank_transfer_data}
                    </div>
                </div>
            )}
        </div>
    );
}

export { isCouponMethod };
